import { useState, useEffect } from "react";
import { API, useAuth, authFetch } from "@/App";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Plus, LayoutList, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function AddToListDialog({ album, open, onOpenChange }) {
  const { user } = useAuth();
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedLists, setSelectedLists] = useState(new Set());
  const [showCreateNew, setShowCreateNew] = useState(false);
  const [newListName, setNewListName] = useState("");
  const [creatingList, setCreatingList] = useState(false);

  useEffect(() => {
    if (open && user) {
      fetchLists();
    }
  }, [open, user]);

  const fetchLists = async () => {
    setLoading(true);
    try {
      const response = await authFetch(`${API}/lists`);
      if (response.ok) {
        const data = await response.json();
        setLists(data);
        
        // Pre-select lists that already contain this album
        const albumId = album.album_id;
        const listsWithAlbum = new Set(
          data
            .filter(list => list.album_ids?.includes(albumId))
            .map(list => list.list_id)
        );
        setSelectedLists(listsWithAlbum);
      }
    } catch (error) {
      console.error("Error fetching lists:", error);
    } finally {
      setLoading(false);
    }
  };

  const toggleList = (listId) => {
    const newSelected = new Set(selectedLists);
    if (newSelected.has(listId)) {
      newSelected.delete(listId);
    } else {
      newSelected.add(listId);
    }
    setSelectedLists(newSelected);
  };

  const handleSave = async () => {
    setSaving(true);
    const albumId = album.album_id;
    
    try {
      // Find lists to add to and remove from
      const originalLists = new Set(
        lists
          .filter(list => list.album_ids?.includes(albumId))
          .map(list => list.list_id)
      );

      const listsToAdd = [...selectedLists].filter(id => !originalLists.has(id));
      const listsToRemove = [...originalLists].filter(id => !selectedLists.has(id));

      // Add album to new lists
      for (const listId of listsToAdd) {
        await authFetch(`${API}/lists/${listId}/albums/${albumId}`, {
          method: "POST",
        });
      }

      // Remove album from deselected lists
      for (const listId of listsToRemove) {
        await authFetch(`${API}/lists/${listId}/albums/${albumId}`, {
          method: "DELETE",
        });
      }

      if (listsToAdd.length > 0 || listsToRemove.length > 0) {
        toast.success("Lists updated!");
      }
      
      onOpenChange(false);
    } catch (error) {
      toast.error("Failed to update lists");
    } finally {
      setSaving(false);
    }
  };

  const handleCreateList = async () => {
    if (!newListName.trim()) {
      toast.error("Please enter a list name");
      return;
    }

    setCreatingList(true);
    try {
      const response = await authFetch(`${API}/lists`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: newListName.trim(),
          description: "",
          is_public: true,
          album_ids: [album.album_id],
        }),
      });

      if (response.ok) {
        toast.success(`Added to "${newListName}"!`);
        setNewListName("");
        setShowCreateNew(false);
        onOpenChange(false);
      } else {
        toast.error("Failed to create list");
      }
    } catch (error) {
      toast.error("Something went wrong");
    } finally {
      setCreatingList(false);
    }
  };

  if (!user) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="bg-[#1a1a2e] border-white/10 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-white">Sign in required</DialogTitle>
          </DialogHeader>
          <p className="text-white/60 text-sm">
            Please sign in to add albums to your lists.
          </p>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#1a1a2e] border-white/10 max-w-sm">
        <DialogHeader>
          <DialogTitle className="text-white">Add to List</DialogTitle>
        </DialogHeader>
        
        {/* Album preview */}
        <div className="flex items-center gap-3 py-2 border-b border-white/10">
          <img
            src={album.cover_xl || album.cover_big || album.cover_medium || album.cover_url || album.cover_small}
            alt={album.title}
            className="w-12 h-12 rounded object-cover"
          />
          <div className="flex-1 min-w-0">
            <p className="font-medium text-white truncate">{album.title}</p>
            <p className="text-sm text-white/60 truncate">{album.artist}</p>
          </div>
        </div>

        {loading ? (
          <div className="py-8 flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-purple-400" />
          </div>
        ) : (
          <>
            {/* Lists */}
            <ScrollArea className="max-h-64">
              <div className="space-y-1">
                {lists.length > 0 ? (
                  lists.map((list) => (
                    <button
                      key={list.list_id}
                      onClick={() => toggleList(list.list_id)}
                      className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-white/5 transition-colors text-left"
                      data-testid={`list-option-${list.list_id}`}
                    >
                      <div className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${
                        selectedLists.has(list.list_id) 
                          ? "bg-purple-500 border-purple-500" 
                          : "border-white/30"
                      }`}>
                        {selectedLists.has(list.list_id) && (
                          <Check className="w-3 h-3 text-white" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-white truncate">{list.name}</p>
                        <p className="text-xs text-white/50">{list.album_ids?.length || 0} albums</p>
                      </div>
                    </button>
                  ))
                ) : (
                  <div className="py-6 text-center">
                    <LayoutList className="w-8 h-8 text-white/30 mx-auto mb-2" />
                    <p className="text-sm text-white/50">No lists yet</p>
                  </div>
                )}
              </div>
            </ScrollArea>

            {/* Create new list */}
            {showCreateNew ? (
              <div className="flex gap-2 pt-2 border-t border-white/10">
                <Input
                  placeholder="New list name..."
                  value={newListName}
                  onChange={(e) => setNewListName(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleCreateList()}
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
                  autoFocus
                  data-testid="new-list-name-input"
                />
                <Button
                  onClick={handleCreateList}
                  disabled={creatingList}
                  size="sm"
                  className="shrink-0"
                  data-testid="create-new-list-btn"
                >
                  {creatingList ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add"}
                </Button>
              </div>
            ) : (
              <button
                onClick={() => setShowCreateNew(true)}
                className="w-full flex items-center gap-2 p-3 rounded-lg hover:bg-white/5 transition-colors text-purple-400"
                data-testid="show-create-list-btn"
              >
                <Plus className="w-5 h-5" />
                <span>Create new list</span>
              </button>
            )}

            {/* Save button */}
            {lists.length > 0 && (
              <Button
                onClick={handleSave}
                disabled={saving}
                className="w-full rounded-full mt-2"
                data-testid="save-to-lists-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {saving ? "Saving..." : "Done"}
              </Button>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
